import React, { useState, useEffect } from 'react';
import { Calculator, Users, UserPlus, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CustomerReferralRateCalculator = () => {
  const [totalCustomers, setTotalCustomers] = useState(1000);
  const [referrals, setReferrals] = useState(50);
  const [referralRate, setReferralRate] = useState(0);

  useEffect(() => {
    calculateReferralRate();
  }, [totalCustomers, referrals]);

  const calculateReferralRate = () => {
    const rate = (referrals / totalCustomers) * 100;
    setReferralRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-referral-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Customer Referral Rate Calculator: Boost Word-of-Mouth | Free Tool</title>
        <meta name="description" content="Calculate your Customer Referral Rate instantly. Get industry benchmarks, optimization tips, and strategies to improve your referral program performance." />
        <meta name="keywords" content="referral rate calculator, customer referral metrics, word of mouth marketing, referral program analytics, customer advocacy" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Referral Rate Calculator: Boost Word-of-Mouth Marketing" />
        <meta property="og:description" content="Calculate your Customer Referral Rate instantly. Get actionable insights to improve your referral program and increase customer advocacy." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Referral Rate Calculator: Boost Word-of-Mouth Marketing" />
        <meta property="twitter:description" content="Calculate your Customer Referral Rate instantly. Get actionable insights to improve your referral program and increase customer advocacy." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Customer Referral Rate Calculator",
            "description": "Free calculator to measure and optimize customer referral rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Referral Rate",
              "Industry Benchmarks",
              "Referral Program Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Customer Referral Rate",
              "description": "The percentage of customers who refer new customers to your business through word-of-mouth or formal referral programs."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Marketing Managers, Business Owners, Customer Success Teams"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Customer Referral Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of customers"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of referrals"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your referral rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Customer Referral Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Customers</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCustomers}
                  onChange={(e) => setTotalCustomers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Referrals</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserPlus className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={referrals}
                  onChange={(e) => setReferrals(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {referralRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Customer Referral Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Customer Referral Rate is {referralRate}%. This means that {referralRate}% of your customers are actively referring new customers to your business.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 2.3-4.5%</li>
          <li>SaaS: 5-7%</li>
          <li>Financial Services: 2-4%</li>
          <li>Professional Services: 4-6%</li>
          <li>Retail: 2.5-4%</li>
          <li>B2B Services: 5-8%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Referral Program Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Implement formal referral programs</li>
          <li>Offer compelling incentives</li>
          <li>Simplify the referral process</li>
          <li>Ask at the right moment</li>
          <li>Train team for referrals</li>
          <li>Leverage social media</li>
          <li>Show appreciation</li>
          <li>Track program performance</li>
          <li>Optimize referral messaging</li>
          <li>Use multi-channel promotion</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Referral Program Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Referred customers have 16% higher lifetime value</li>
          <li>Referral leads convert 30% better than other leads</li>
          <li>Referred customers are 4x more likely to refer others</li>
          <li>37% higher retention rate for referred customers</li>
          <li>Cost per acquisition 25% lower for referrals</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Double-sided incentives (reward both parties)</li>
          <li>Mobile-optimized referral process</li>
          <li>Clear program terms and conditions</li>
          <li>Automated tracking and rewards</li>
          <li>Regular program communication</li>
          <li>A/B test incentive structures</li>
          <li>Monitor fraud prevention</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerReferralRateCalculator;
